import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd'
import {
  AlipayCircleFilled,
  BankOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import {
  addBankAccount,
  addPersonalPayoutAccount,
  createPayoutEwallet,
  deleteBankAccount,
  deletePersonalPayoutAccount,
  deletePayoutEwallet,
  getBankAccounts,
  getPayoutEwallet,
  getPersonalPayoutAccounts,
} from '../../services/payout'
import type { PayoutEwallet } from '../../services/payout'
import { getPersonalKycStatus } from '../../services/kyc'
import { useAuthStore } from '../../stores/authStore'
import type { BankAccount, PersonalPayoutAccount } from '../../types'
import './BankAccounts.css'

const { Text, Title } = Typography

const REGION_OPTIONS = [
  { value: 'US', label: '美國（US）' },
  { value: 'HK', label: '中國香港（HK）' },
  { value: 'SG', label: '新加坡（SG）' },
]

const COUNTRY_OPTIONS = [
  { value: 'CN', label: '🇨🇳 中國' },
  { value: 'HK', label: '🇭🇰 中國香港' },
  { value: 'SG', label: '🇸🇬 新加坡' },
  { value: 'US', label: '🇺🇸 美國' },
]

const PERSONAL_BANK_COUNTRIES = [
  { value: 'CN', label: '🇨🇳 中國', phoneCode: '+86' },
  { value: 'HK', label: '🇭🇰 中國香港', phoneCode: '+852' },
  { value: 'MO', label: '🇲🇴 中國澳門', phoneCode: '+853' },
  { value: 'TW', label: '🇹🇼 中國台灣', phoneCode: '+886' },
  { value: 'US', label: '🇺🇸 美國', phoneCode: '+1' },
  { value: 'SG', label: '🇸🇬 新加坡', phoneCode: '+65' },
  { value: 'GB', label: '🇬🇧 英國', phoneCode: '+44' },
  { value: 'FR', label: '🇫🇷 法國', phoneCode: '+33' },
  { value: 'JP', label: '🇯🇵 日本', phoneCode: '+81' },
  { value: 'KR', label: '🇰🇷 韓國', phoneCode: '+82' },
]

const PHONE_CODE_OPTIONS = PERSONAL_BANK_COUNTRIES.map(({ label, phoneCode }) => ({
  value: phoneCode,
  label: `${label.split(' ')[0]} ${phoneCode}`,
}))

const EWALLET_METHODS = [
  { value: 'ALIPAY', label: 'Alipay', currency: 'CNY', fieldKey: 'account_no', fieldLabel: '支付寶賬號（手機號或郵箱）' },
  { value: 'PAYPAL', label: 'PayPal', currency: 'USD', fieldKey: 'account_no', fieldLabel: 'PayPal 賬號郵箱' },
]

const ewalletMethodForCountry = (country?: string) => country === 'CN' ? 'ALIPAY' : 'PAYPAL'

const ewalletMethodsForCountry = (country?: string) => {
  const method = ewalletMethodForCountry(country)
  return EWALLET_METHODS.filter((item) => item.value === method)
}

const ENGLISH_LETTERS_ONLY_PATTERN = /^[A-Za-z]+$/
const ENGLISH_BANK_NAME_PATTERN = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/
const CHINESE_NAME_PATTERN = /^[\u3400-\u9FFF]+$/
const MOBILE_PATTERN = /^\+\d{8,15}$/
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/

interface PersonalKycStatus {
  status?: string
}

interface RecipientValues {
  recipientType: 'INDIVIDUAL' | 'BUSINESS'
  accountKind: 'BANK' | 'E_WALLET'
  country: string
  currency: string
  firstName?: string
  lastName?: string
  localFirstName?: string
  localLastName?: string
  phoneCountryCode?: string
  phoneNumber?: string
  address?: string
}

interface PersonalAccountValues {
  paymentMethod: string
  accountNo?: string
  bankAccountNo?: string
  bankName?: string
  bankCode?: string
  certNumber?: string
  isDefault?: boolean
}

export default function BankAccounts() {
  const [accounts, setAccounts] = useState<BankAccount[]>([])
  const [personalAccounts, setPersonalAccounts] = useState<PersonalPayoutAccount[]>([])
  const [payoutEwallet, setPayoutEwallet] = useState<PayoutEwallet | null>(null)
  const [payoutEwalletLoading, setPayoutEwalletLoading] = useState(true)
  const [payoutEwalletLoadError, setPayoutEwalletLoadError] = useState(false)
  const [personalKycStatus, setPersonalKycStatus] = useState<PersonalKycStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [region, setRegion] = useState('US')
  const [personalModalVisible, setPersonalModalVisible] = useState(false)
  const [payoutEwalletModalVisible, setPayoutEwalletModalVisible] = useState(false)
  const [deletePayoutEwalletModalVisible, setDeletePayoutEwalletModalVisible] = useState(false)
  const [personalStep, setPersonalStep] = useState(0)
  const [personalKind, setPersonalKind] = useState<'BANK' | 'E_WALLET'>('E_WALLET')
  const [ewalletMethod, setEwalletMethod] = useState('ALIPAY')
  const [recipientValues, setRecipientValues] = useState<RecipientValues | null>(null)
  const [accountValues, setAccountValues] = useState<Partial<PersonalAccountValues>>({ paymentMethod: 'ALIPAY' })
  const [form] = Form.useForm()
  const [recipientForm] = Form.useForm<RecipientValues>()
  const [accountForm] = Form.useForm<PersonalAccountValues>()
  const [payoutEwalletForm] = Form.useForm<{ chain: PayoutEwallet['chain']; address: string; totpCode: string }>()
  const [deletePayoutEwalletForm] = Form.useForm<{ totpCode: string }>()
  const twoFactorEnabled = useAuthStore((state) => state.merchant?.twoFactorEnabled === true)

  const personalKycApproved = personalKycStatus?.status === 'APPROVED'
  const personalBankCountry = Form.useWatch('country', recipientForm)
  const isInternationalBank = personalKind === 'BANK' &&
    (personalStep === 0 ? personalBankCountry : recipientValues?.country) !== 'CN'

  const loadPayoutEwallet = async () => {
    setPayoutEwalletLoading(true)
    setPayoutEwalletLoadError(false)
    try {
      const response = await getPayoutEwallet()
      setPayoutEwallet(response.data ?? null)
    } catch {
      setPayoutEwalletLoadError(true)
    } finally {
      setPayoutEwalletLoading(false)
    }
  }

  const fetchAccounts = () => {
    setLoading(true)
    Promise.all([getBankAccounts(), getPersonalPayoutAccounts(), getPersonalKycStatus()])
      .then(([bankRes, personalRes, kycRes]) => {
        setAccounts(bankRes.data)
        setPersonalAccounts(personalRes.data)
        setPersonalKycStatus(kycRes.data)
      })
      .catch(() => message.error('賬戶資料載入失敗'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchAccounts()
    loadPayoutEwallet()
  }, [])

  const handleAdd = async (values: {
    accountHolderName: string
    bankName: string
    bankCode: string
    branchCode?: string
    accountNumber: string
    region: string
    fedWire?: string
    bankAddress?: string
    isDefault?: boolean
  }) => {
    setSubmitting(true)
    try {
      await addBankAccount(values)
      message.success('銀行賬戶添加成功')
      setModalVisible(false)
      form.resetFields()
      setRegion('US')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteBankAccount(id)
      message.success('銀行賬戶已刪除')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    }
  }

  const handleDeletePersonalAccount = async (id: string) => {
    try {
      await deletePersonalPayoutAccount(id)
      message.success('個人提款賬戶已刪除')
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    }
  }

  const handleCreatePayoutEwallet = async (values: {
    chain: PayoutEwallet['chain']
    address: string
    totpCode: string
  }) => {
    setSubmitting(true)
    try {
      const response = await createPayoutEwallet({
        chain: values.chain,
        address: values.address.trim(),
        totpCode: values.totpCode,
      })
      setPayoutEwallet(response.data)
      setPayoutEwalletModalVisible(false)
      payoutEwalletForm.resetFields()
      message.success('海外電子錢包地址已添加')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeletePayoutEwallet = async (values: { totpCode: string }) => {
    setSubmitting(true)
    try {
      await deletePayoutEwallet({ totpCode: values.totpCode })
      setPayoutEwallet(null)
      setDeletePayoutEwalletModalVisible(false)
      deletePayoutEwalletForm.resetFields()
      message.success('海外電子錢包地址已刪除')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '刪除失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const copyPayoutEwalletAddress = async () => {
    if (!payoutEwallet) return
    try {
      await navigator.clipboard.writeText(payoutEwallet.address)
      message.success('地址已複製')
    } catch {
      message.error('無法複製地址')
    }
  }

  const handleCancel = () => {
    setModalVisible(false)
    form.resetFields()
    setRegion('US')
  }

  const openPersonalModal = () => {
    setPersonalStep(0)
    setPersonalKind('E_WALLET')
    setEwalletMethod('ALIPAY')
    setRecipientValues(null)
    setAccountValues({ paymentMethod: 'ALIPAY' })
    setPersonalModalVisible(true)
  }

  const closePersonalModal = () => {
    setPersonalModalVisible(false)
    recipientForm.resetFields()
    accountForm.resetFields()
    setPersonalStep(0)
    setRecipientValues(null)
    setAccountValues({ paymentMethod: 'ALIPAY' })
  }

  const handleRecipientNext = async () => {
    const values = await recipientForm.validateFields()
    const normalizedValues = {
      ...values,
      firstName: values.firstName?.trim(),
      lastName: values.lastName?.trim(),
      localFirstName: values.localFirstName?.trim(),
      localLastName: values.localLastName?.trim(),
    }
    if (values.accountKind === 'E_WALLET') {
      const method = ewalletMethodForCountry(values.country)
      setEwalletMethod(method)
      setAccountValues((prev) => ({ ...prev, paymentMethod: method }))
      accountForm.setFieldValue('paymentMethod', method)
    }
    setRecipientValues(normalizedValues)
    setPersonalStep(1)
  }

  const handlePersonalSave = async () => {
    if (!personalKycApproved) {
      message.error('請先完成並通過個人 KYC，再添加個人提款賬戶')
      return
    }

    const recipient = recipientValues
    if (!recipient || !recipient.country || (recipient.accountKind === 'BANK' && !recipient.currency)) {
      message.error('請先完成收款人信息')
      setPersonalStep(0)
      return
    }

    const account = {
      ...accountValues,
      ...accountForm.getFieldsValue(true),
    }
    const fullName = [recipient.firstName, recipient.lastName].filter(Boolean).join(' ')

    setSubmitting(true)
    try {
      if (recipient.accountKind === 'E_WALLET') {
        const accountNo = account.accountNo?.trim()
        if (!accountNo) {
          accountForm.setFields([{ name: 'accountNo', errors: ['請輸入收款賬號'] }])
          message.error('請輸入收款賬號')
          setSubmitting(false)
          return
        }

        const expectedMethod = ewalletMethodForCountry(recipient.country)
        const method = EWALLET_METHODS.find((item) => item.value === expectedMethod) ?? EWALLET_METHODS[0]
        await addPersonalPayoutAccount({
          accountKind: 'E_WALLET',
          paymentMethod: method.value,
          country: recipient.country,
          currency: method.currency,
          accountHolderName: fullName,
          fields: [
            {
              key: method.fieldKey,
              value: accountNo,
              displayValue: accountNo,
            },
          ],
        })
      } else {
        const bankAccountNo = account.bankAccountNo?.trim()
        const certNumber = account.certNumber?.trim()
        const bankName = account.bankName?.trim()
        const bankCode = account.bankCode?.trim().toUpperCase()
        const isChinaBank = recipient.country === 'CN'
        const localFullName = [recipient.localLastName, recipient.localFirstName].filter(Boolean).join('')
        const mobile = `${recipient.phoneCountryCode || ''}${(recipient.phoneNumber || '').trim()}`
        const fieldErrors: Parameters<typeof accountForm.setFields>[0] = []
        if (!bankAccountNo) fieldErrors.push({ name: 'bankAccountNo', errors: ['請輸入銀行賬號'] })
        if (isChinaBank && !certNumber) fieldErrors.push({ name: 'certNumber', errors: ['請輸入身份證號'] })
        if (!isChinaBank && !bankName) fieldErrors.push({ name: 'bankName', errors: ['請輸入銀行名稱'] })
        if (!isChinaBank && (!bankCode || !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(bankCode))) {
          fieldErrors.push({ name: 'bankCode', errors: ['請輸入有效的 SWIFT/BIC 代碼'] })
        }
        if (isChinaBank && !CHINESE_NAME_PATTERN.test(localFullName)) {
          recipientForm.setFields([
            { name: 'localFirstName', errors: ['請輸入中文名字'] },
            { name: 'localLastName', errors: ['請輸入中文姓氏'] },
          ])
          message.error('中國銀行賬戶姓名只能使用中文')
          setPersonalStep(0)
          setSubmitting(false)
          return
        }
        if (!MOBILE_PATTERN.test(mobile)) {
          recipientForm.setFields([{ name: 'phoneNumber', errors: ['請輸入有效的手機號'] }])
          message.error('請輸入有效的手機號')
          setPersonalStep(0)
          setSubmitting(false)
          return
        }
        if (fieldErrors.length > 0) {
          accountForm.setFields(fieldErrors)
          message.error('請完成收款方式信息')
          setSubmitting(false)
          return
        }

        await addPersonalPayoutAccount({
          accountKind: 'BANK',
          paymentMethod: 'BANK_TRANSFER',
          country: recipient.country,
          currency: recipient.currency,
          accountHolderName: isChinaBank ? localFullName : fullName,
          firstName: isChinaBank ? recipient.localFirstName : recipient.firstName,
          lastName: isChinaBank ? recipient.localLastName : recipient.lastName,
          mobile,
          accountNumber: bankAccountNo,
          bankName: isChinaBank ? undefined : bankName,
          bankCode: isChinaBank ? undefined : bankCode,
          clearingNetwork: isChinaBank ? undefined : 'SWIFT',
          certNumber: isChinaBank ? certNumber : undefined,
        })
      }
      message.success('個人提款賬戶已添加')
      closePersonalModal()
      fetchAccounts()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '添加失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const columns = [
    { title: '企業/賬戶名稱', dataIndex: 'accountHolderName', key: 'accountHolderName' },
    { title: '銀行', dataIndex: 'bankName', key: 'bankName' },
    { title: 'SWIFT/BIC', dataIndex: 'bankCode', key: 'bankCode', render: (v: string) => v || '-' },
    { title: '分行代碼', dataIndex: 'branchCode', key: 'branchCode', render: (v: string) => v || '-' },
    { title: '賬號', key: 'accountNumber', render: (_: unknown, record: BankAccount) => `****${record.accountNumberLast4}` },
    {
      title: '地區',
      dataIndex: 'region',
      key: 'region',
      render: (r: string) => REGION_OPTIONS.find((o) => o.value === r)?.label ?? r,
    },
    { title: 'FedWire', dataIndex: 'fedWire', key: 'fedWire', render: (v: string) => v || '-' },
    {
      title: '狀態',
      key: 'status',
      render: (_: unknown, record: BankAccount) => (
        <>
          {record.isDefault && <Tag color="blue">預設</Tag>}
          {record.isVerified && <Tag color="green">已驗證</Tag>}
        </>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: BankAccount) => (
        <Popconfirm title="確定要刪除此銀行賬戶嗎？" onConfirm={() => handleDelete(record.id)}>
          <Button type="link" danger>刪除</Button>
        </Popconfirm>
      ),
    },
  ]

  const renderRecipientStep = () => (
    <Form
      form={recipientForm}
      layout="vertical"
      requiredMark
      initialValues={{
        recipientType: 'INDIVIDUAL',
        accountKind: 'E_WALLET',
        country: 'CN',
        currency: 'CNY',
        phoneCountryCode: '+86',
      }}
    >
      <Form.Item name="recipientType" label="您需要付款給誰？" rules={[{ required: true }]}>
        <Radio.Group>
          <Radio value="INDIVIDUAL">個人</Radio>
          <Radio value="BUSINESS" disabled>企業</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="accountKind" label="收款賬戶類型" rules={[{ required: true }]}>
        <Radio.Group
          onChange={(event) => {
            const value = event.target.value as 'BANK' | 'E_WALLET'
            setPersonalKind(value)
            setRecipientValues(null)
            if (value === 'BANK') {
              recipientForm.setFieldsValue({ country: 'CN', currency: 'CNY', phoneCountryCode: '+86', firstName: undefined, lastName: undefined })
              setAccountValues({ paymentMethod: 'BANK_TRANSFER' })
            } else {
              setEwalletMethod('ALIPAY')
              recipientForm.setFieldsValue({ country: 'CN', currency: 'CNY', phoneCountryCode: '+86', localFirstName: undefined, localLastName: undefined })
              setAccountValues({ paymentMethod: 'ALIPAY', accountNo: undefined })
            }
            accountForm.resetFields()
          }}
        >
          <Radio value="E_WALLET">電子錢包</Radio>
          <Radio value="BANK">銀行賬戶</Radio>
        </Radio.Group>
      </Form.Item>

      <div style={{ display: 'grid', gridTemplateColumns: personalKind === 'BANK' ? '1fr 1fr' : '1fr', gap: 24 }}>
        <Form.Item name="country" label={personalKind === 'BANK' ? '銀行所在國家或地區' : '所在國家或地區'} rules={[{ required: true }]}>
          <Select
            options={personalKind === 'BANK' ? PERSONAL_BANK_COUNTRIES : COUNTRY_OPTIONS}
            onChange={(country) => {
              if (personalKind === 'BANK') {
                recipientForm.setFieldsValue({
                  currency: country === 'CN' ? 'CNY' : 'USD',
                  phoneCountryCode: PERSONAL_BANK_COUNTRIES.find((option) => option.value === country)?.phoneCode,
                  phoneNumber: undefined,
                  firstName: undefined,
                  lastName: undefined,
                  localFirstName: undefined,
                  localLastName: undefined,
                })
                accountForm.resetFields()
                setAccountValues({ paymentMethod: 'BANK_TRANSFER' })
                return
              }
              const method = ewalletMethodForCountry(country)
              setEwalletMethod(method)
              setAccountValues((prev) => ({ ...prev, paymentMethod: method }))
              accountForm.setFieldValue('paymentMethod', method)
            }}
          />
        </Form.Item>
        {personalKind === 'BANK' && (
          <Form.Item name="currency" label="幣種" rules={[{ required: true }]}>
            <Select options={[{ value: isInternationalBank ? 'USD' : 'CNY', label: isInternationalBank ? 'USD' : 'CNY' }]} />
          </Form.Item>
        )}
      </div>

      {personalKind === 'BANK' && !isInternationalBank ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <Form.Item
              name="localFirstName"
              label="名字"
              rules={[
                { required: true, whitespace: true, message: '請輸入中文名字' },
                { pattern: CHINESE_NAME_PATTERN, message: '名字只能輸入中文' },
              ]}
            >
              <Input placeholder="請輸入中文名字，如“三丰”" />
            </Form.Item>
            <Form.Item
              name="localLastName"
              label="姓氏"
              rules={[
                { required: true, whitespace: true, message: '請輸入中文姓氏' },
                { pattern: CHINESE_NAME_PATTERN, message: '姓氏只能輸入中文' },
              ]}
            >
              <Input placeholder="請輸入中文姓氏，如“張”" />
            </Form.Item>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <Form.Item
            name="firstName"
            label={isInternationalBank ? '英文名字' : '名字'}
            rules={[
              { required: true, whitespace: true, message: '請輸入名字的英文或拼音' },
              { pattern: isInternationalBank ? ENGLISH_BANK_NAME_PATTERN : ENGLISH_LETTERS_ONLY_PATTERN, message: isInternationalBank ? '英文名字可包含空格、連字符或撇號' : '名字只能輸入英文字母 A-Z/a-z' },
            ]}
          >
            <Input placeholder="請輸入名字的英文或拼音" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={isInternationalBank ? '英文姓氏' : '姓氏'}
            rules={[
              { required: true, whitespace: true, message: '請輸入姓氏的英文或拼音' },
              { pattern: isInternationalBank ? ENGLISH_BANK_NAME_PATTERN : ENGLISH_LETTERS_ONLY_PATTERN, message: isInternationalBank ? '英文姓氏可包含空格、連字符或撇號' : '姓氏只能輸入英文字母 A-Z/a-z' },
            ]}
          >
            <Input placeholder="請輸入姓氏的英文或拼音" />
          </Form.Item>
        </div>
      )}
      {personalKind === 'BANK' && (
        <>
          <Form.Item label="聯繫電話（手機號）" required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="phoneCountryCode" noStyle rules={[{ required: true }]}>
                <Select options={PHONE_CODE_OPTIONS} style={{ width: 190 }} />
              </Form.Item>
              <Form.Item name="phoneNumber" noStyle rules={[
                { required: true, whitespace: true, message: '請輸入手機號' },
                { pattern: /^\d{6,14}$/, message: '手機號只能包含數字' },
              ]}>
                <Input data-testid="personal-bank-mobile" />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="address" label="地址（選填）">
            <Input />
          </Form.Item>
        </>
      )}
    </Form>
  )

  const renderAccountStep = () => (
    <Form
      form={accountForm}
      layout="vertical"
      initialValues={{ paymentMethod: 'ALIPAY' }}
      onValuesChange={(_, values) => setAccountValues((prev) => ({ ...prev, ...values }))}
    >
      {personalKind === 'E_WALLET' ? (
        <>
          <Text type="secondary">選擇要開通的收款方式</Text>
          <div style={{ margin: '12px 0 24px' }}>
            <Segmented
              value={ewalletMethod}
              options={ewalletMethodsForCountry(recipientValues?.country).map((method) => ({
                value: method.value,
                label: <Space>{method.value === 'ALIPAY' ? <AlipayCircleFilled style={{ color: '#1677ff' }} /> : <WalletOutlined />}{method.label}</Space>,
              }))}
              onChange={(value) => {
                const method = String(value)
                setEwalletMethod(method)
                setAccountValues((prev) => ({ ...prev, paymentMethod: method }))
                accountForm.setFieldValue('paymentMethod', method)
              }}
            />
          </div>
          <Card
            size="small"
            title={<Space>{ewalletMethod === 'ALIPAY' ? <AlipayCircleFilled style={{ color: '#1677ff' }} /> : <WalletOutlined />}{EWALLET_METHODS.find((item) => item.value === ewalletMethod)?.label}</Space>}
            extra={<Text strong>到賬幣種：{EWALLET_METHODS.find((item) => item.value === ewalletMethod)?.currency}</Text>}
          >
            <Form.Item name="paymentMethod" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              name="accountNo"
              label={EWALLET_METHODS.find((item) => item.value === ewalletMethod)?.fieldLabel || '收款賬號'}
              rules={[{ required: true, message: '請輸入收款賬號' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="isDefault" valuePropName="checked">
              <Radio>是否作為默認收款賬戶</Radio>
            </Form.Item>
          </Card>
        </>
      ) : (
        <Card size="small" title={<Space><BankOutlined />Bank Account</Space>}>
          {isInternationalBank && (
            <>
              <Form.Item name="bankName" label="銀行名稱" rules={[{ required: true, whitespace: true, message: '請輸入銀行名稱' }]}>
                <Input placeholder="Bank Name" />
              </Form.Item>
              <Form.Item name="bankCode" label="SWIFT/BIC 代碼" rules={[
                { required: true, whitespace: true, message: '請輸入 SWIFT/BIC 代碼' },
                { pattern: /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/, message: 'SWIFT/BIC 代碼須為 8 或 11 位' },
              ]}>
                <Input placeholder="例如 HSBCHKHH" />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="bankAccountNo"
            label="銀行賬號"
            rules={[
              { required: true, whitespace: true, message: '請輸入銀行賬號' },
              ...(!isInternationalBank ? [{ pattern: /^\d{16,19}$/, message: '銀行賬號必須是16到19位數字' }] : []),
            ]}
          >
            <Input />
          </Form.Item>
          {!isInternationalBank && (
            <Form.Item
              name="certNumber"
              label="身份證號"
              rules={[
                { required: true, message: '請輸入身份證號' },
                { pattern: /^(\d{15}|\d{17}[\dX])$/, message: '身份證號長度必須是15或18位，若包含X，請填寫大寫X' },
              ]}
            >
              <Input />
            </Form.Item>
          )}
        </Card>
      )}
    </Form>
  )

  return (
    <div className="bank-accounts-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>銀行賬戶</Title>
      </div>

      <Card
        className="bank-account-section"
        title={<Space><WalletOutlined />海外電子錢包</Space>}
        extra={!payoutEwallet ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!twoFactorEnabled}
            onClick={() => setPayoutEwalletModalVisible(true)}
          >
            添加地址
          </Button>
        ) : undefined}
        style={{ marginBottom: 16 }}
        loading={payoutEwalletLoading}
      >
        {payoutEwalletLoadError && (
          <Alert
            type="error"
            showIcon
            message="海外電子錢包地址載入失敗"
            action={<Button size="small" onClick={loadPayoutEwallet}>重新載入</Button>}
            style={{ marginBottom: 16 }}
          />
        )}
        {!twoFactorEnabled && (
          <Alert
            type="warning"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message="添加或刪除地址前需要啟用 2FA"
            action={<Button type="link" href="/dashboard/settings">前往安全設置</Button>}
            style={{ marginBottom: payoutEwallet ? 16 : 0 }}
          />
        )}
        {!payoutEwalletLoadError && payoutEwallet ? (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="鏈">{payoutEwallet.chain}</Descriptions.Item>
            <Descriptions.Item label="錢包地址">
              <Space wrap>
                <Text>{payoutEwallet.address}</Text>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  title="複製地址"
                  onClick={copyPayoutEwalletAddress}
                />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="操作">
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!twoFactorEnabled}
                onClick={() => setDeletePayoutEwalletModalVisible(true)}
              >
                刪除地址
              </Button>
            </Descriptions.Item>
          </Descriptions>
        ) : !payoutEwalletLoadError && twoFactorEnabled ? (
          <Text type="secondary">尚未添加海外電子錢包地址</Text>
        ) : null}
      </Card>

      <Card
        className="bank-account-section"
        title={<Space><BankOutlined />企業銀行賬戶</Space>}
        extra={(
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
            data-testid="add-bank-account-btn"
          >
            添加企業賬戶
          </Button>
        )}
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Card
        className="bank-account-section"
        title={<Space><UserOutlined />個人提款賬戶</Space>}
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={openPersonalModal}>
            添加個人提款賬戶
          </Button>
        )}
      >
        <Table
          dataSource={personalAccounts}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: '類型', dataIndex: 'accountKind', render: (v) => v === 'BANK' ? '銀行賬戶' : '電子錢包' },
            { title: '方式', dataIndex: 'paymentMethod' },
            { title: '幣種', dataIndex: 'currency' },
            {
              title: '賬戶',
              render: (_: unknown, r: PersonalPayoutAccount) =>
                r.accountKind === 'BANK'
                  ? `${r.bankName || 'Bank Account'} ****${r.accountNumberLast4 || ''}`
                  : (r.fields?.[0]?.displayValue || r.fields?.[0]?.value || '-'),
            },
            {
              title: '操作',
              key: 'actions',
              render: (_: unknown, record: PersonalPayoutAccount) => (
                <Popconfirm title="確定要刪除此個人提款賬戶嗎？" onConfirm={() => handleDeletePersonalAccount(record.id)}>
                  <Button type="link" danger>刪除</Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="添加海外電子錢包地址"
        open={payoutEwalletModalVisible}
        onCancel={() => {
          setPayoutEwalletModalVisible(false)
          payoutEwalletForm.resetFields()
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={payoutEwalletForm}
          layout="vertical"
          initialValues={{ chain: 'TRON' }}
          onFinish={handleCreatePayoutEwallet}
        >
          <Form.Item name="chain" label="鏈" rules={[{ required: true, message: '請選擇鏈' }]}>
            <Select options={[
              { value: 'TRON', label: 'TRON' },
              { value: 'BSC', label: 'BSC' },
              { value: 'ETH', label: 'ETH' },
            ]} />
          </Form.Item>
          <Form.Item
            name="address"
            label="錢包地址"
            dependencies={['chain']}
            rules={[
              { required: true, whitespace: true, message: '請輸入錢包地址' },
              ({ getFieldValue }) => ({
                validator(_, value: string) {
                  if (!value) return Promise.resolve()
                  const chain = getFieldValue('chain') as PayoutEwallet['chain']
                  const valid = chain === 'TRON'
                    ? TRON_ADDRESS_PATTERN.test(value.trim())
                    : EVM_ADDRESS_PATTERN.test(value.trim())
                  return valid ? Promise.resolve() : Promise.reject(new Error('錢包地址與所選鏈不匹配'))
                },
              }),
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="totpCode"
            label="2FA 驗證碼"
            rules={[
              { required: true, message: '請輸入 2FA 驗證碼' },
              { pattern: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' },
            ]}
          >
            <Input inputMode="numeric" maxLength={6} autoComplete="one-time-code" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
            <Button onClick={() => setPayoutEwalletModalVisible(false)}>取消</Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="確認刪除海外電子錢包地址"
        open={deletePayoutEwalletModalVisible}
        onCancel={() => {
          setDeletePayoutEwalletModalVisible(false)
          deletePayoutEwalletForm.resetFields()
        }}
        footer={null}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message="刪除後，新的 USDT 結款將無法提交，直至重新添加地址。"
          style={{ marginBottom: 16 }}
        />
        <Form form={deletePayoutEwalletForm} layout="vertical" onFinish={handleDeletePayoutEwallet}>
          <Form.Item
            name="totpCode"
            label="2FA 驗證碼"
            rules={[
              { required: true, message: '請輸入 2FA 驗證碼' },
              { pattern: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' },
            ]}
          >
            <Input inputMode="numeric" maxLength={6} autoComplete="one-time-code" />
          </Form.Item>
          <Space>
            <Button danger type="primary" htmlType="submit" loading={submitting}>確認刪除</Button>
            <Button onClick={() => setDeletePayoutEwalletModalVisible(false)}>取消</Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="添加銀行賬戶（企業賬戶）"
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={520}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdd}
          initialValues={{ region: 'US' }}
        >
          <Form.Item name="accountHolderName" label="企業/賬戶名稱" rules={[{ required: true, message: '請輸入企業/賬戶名稱' }]}>
            <Input placeholder="Enterprise Name" />
          </Form.Item>
          <Form.Item name="bankName" label="銀行名稱" rules={[{ required: true, message: '請輸入銀行名稱' }]}>
            <Input placeholder="Bank Name" />
          </Form.Item>
          <Form.Item name="bankCode" label="SWIFT / BIC 代碼" rules={[{ required: true, message: '請輸入 SWIFT/BIC 代碼' }]}>
            <Input placeholder="e.g. CHASUS33" />
          </Form.Item>
          <Form.Item name="branchCode" label="分行代碼（Branch Code）">
            <Input placeholder="e.g. 004 (香港匯豐分行代碼)" />
          </Form.Item>
          <Form.Item name="accountNumber" label="銀行賬號" rules={[{ required: true, message: '請輸入銀行賬號' }]}>
            <Input placeholder="Account Number" />
          </Form.Item>
          <Form.Item name="region" label="地區" rules={[{ required: true, message: '請選擇地區' }]}>
            <Select
              options={REGION_OPTIONS}
              onChange={(val) => {
                setRegion(val)
                if (val !== 'US') form.setFieldValue('fedWire', undefined)
              }}
            />
          </Form.Item>
          {region === 'US' && (
            <Form.Item name="fedWire" label="FedWire 路由號（ABA）" rules={[{ required: true, message: '請輸入 FedWire 路由號' }]}>
              <Input placeholder="e.g. 021000021" />
            </Form.Item>
          )}
          <Form.Item name="bankAddress" label="銀行地址（Bank Address）">
            <Input.TextArea placeholder="e.g. 1 Queen's Road Central, Hong Kong" rows={2} />
          </Form.Item>
          <Form.Item style={{ marginTop: 8 }}>
            <Button type="primary" htmlType="submit" loading={submitting} style={{ marginRight: 8 }}>
              添加
            </Button>
            <Button onClick={handleCancel}>取消</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="添加個人提款賬戶"
        open={personalModalVisible}
        onCancel={closePersonalModal}
        width={900}
        footer={
          <Space>
            <Button onClick={personalStep === 0 ? closePersonalModal : () => setPersonalStep(0)}>
              {personalStep === 0 ? '取消' : '返回'}
            </Button>
            {personalStep === 0 ? (
              <Button type="primary" onClick={handleRecipientNext}>下一步</Button>
            ) : (
              <Button type="primary" loading={submitting} onClick={handlePersonalSave}>保存</Button>
            )}
          </Space>
        }
      >
        {!personalKycApproved && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="個人 KYC 尚未通過"
            description="個人提款賬戶保存前需要先通過個人 KYC。你仍可查看並準備表單字段。"
          />
        )}
        <Steps
          current={personalStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: '設置收款人信息' },
            { title: '設置收款方式' },
          ]}
        />
        {personalStep === 0 ? renderRecipientStep() : renderAccountStep()}
      </Modal>
    </div>
  )
}
